import { isEmpty } from 'utils/is-empty';

/**
 * Sort the data by current lang
 *
 * @param {Array} data
 * @param {String} lang
 */
function sortCountryCode(data, lang) {
  const countryCode = lang.includes('_') ? lang.split('_')[1] : lang;
  data.sort((a) => (a.country.localeCompare(countryCode) === 0 ? -1 : 1));

  return data;
}

/**
 * Parses the dates of the given data points into
 * moment.js moments and parses lat/lng coordinates.
 * @param {Object} data The data points to preprocess.
 */
export function preprocessData(
  mode: string,
  { data: inputData },
  markerColors: string[],
  markerDefaultColor: string,
  currentLang: string | null,
) {
  let data = inputData;
  if (mode !== 'showroom' && isEmpty(inputData.entries)) {
    data = { entries: inputData };
  }

  const categories = [];
  if (
    (mode === 'showroom' && (isEmpty(data[0]) || isEmpty(data[0].entries))) ||
    (mode !== 'showroom' && isEmpty(data.entries))
  ) {
    return { processedData: [] };
  }
  const baseData = mode === 'showroom' ? data[0] : data;

  const processedData = baseData.entries
    .map((entry) => {
      if (typeof entry.lat === 'string') {
        entry.lat = parseFloat(entry.lat);
      }

      if (typeof entry.lng === 'string') {
        entry.lng = parseFloat(entry.lng);
      }

      if (mode !== 'event' && categories.indexOf(entry.category) < 0) {
        categories.push(entry.category);
      }

      if (!entry.lat || !entry.lng) return null;

      return entry;
    })
    // filter all nulls
    .filter((item) => item);

  if (mode === 'hotel') {
    const sortedData = sortCountryCode(processedData, currentLang ?? '');

    return { processedData: sortedData };
  }

  if (mode !== 'event') {
    const colorCodedCategories: { categoryName: string; color: string }[] = categories.map(
      (entry, index) => ({
        categoryName: entry,
        color: markerColors[index % markerColors.length] || markerDefaultColor,
      }),
    );

    return { processedData, colorCodedCategories };
  }

  return { processedData };
}

/**
 * Filters data based on a provided coordinates. If a max distance is selected,
 * it will be taken into consideration and the data is filtered accordingly based
 * on a computed distance value.
 */
export function filterData(
  googleMapsRef,
  {
    fullDataset,
    coordinates,
    mode = 'product',
    maxDistance = 'none',
    filterSerie,
    filterCategory,
    filterModel,
  },
  currentLang,
) {
  let filteredData;

  if (isEmpty(fullDataset) || !googleMapsRef) {
    return filteredData;
  }

  filteredData = fullDataset?.map((entry) => ({
    ...entry,
    distance: coordinates
      ? googleMapsRef.geometry.spherical.computeDistanceBetween(
          new googleMapsRef.LatLng(coordinates.lat, coordinates.lng),
          new googleMapsRef.LatLng(entry.lat, entry.lng),
        )
      : 0,
  }));

  if (maxDistance !== 'none') {
    filteredData = filteredData.filter((entry) => entry.distance < maxDistance);
  }

  if (!isEmpty(filterCategory)) {
    filteredData = filteredData.filter((entry) =>
      Boolean(entry.products.find((item) => item.category === filterCategory)),
    );
  }

  if (!isEmpty(filterSerie)) {
    filteredData = filteredData.filter((entry) =>
      Boolean(entry.products.find((item) => item.series.includes(filterSerie))),
    );
  }

  if (!isEmpty(filterModel)) {
    filteredData = filteredData.filter((entry) =>
      Boolean(entry.models.find((item) => filterModel.includes(item.product))),
    );
  }

  if (mode === 'hotel') {
    // Sort by country
    filteredData = sortCountryCode(filteredData, currentLang);
  }

  if (mode === 'event') {
    // Sort by date
    filteredData.sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  filteredData.sort((a, b) => a.distance - b.distance);

  return filteredData;
}

/**
 * Formats the given distance for usage in the templates.
 * Will calculate miles, if in miles mode and append the unit.
 * Number format: 1 decimal place if < 100, rounded up if > 100.
 * @param {number} distance The distance in meters.
 * @returns {string} The formatted distance.
 */
export function formatDistance(distance, distanceUnit = 'km', isFilter = false) {
  if (!distance) {
    return null;
  }

  let calculatedDistance = distance / 1000;
  if (distanceUnit === 'mi') {
    calculatedDistance *= 0.621371;
  }

  if (isFilter === true) {
    return calculatedDistance.toFixed(0);
  }

  let formattedDistance = null;
  if (calculatedDistance) {
    if (calculatedDistance < 100) {
      formattedDistance = calculatedDistance.toFixed(1);
    } else {
      formattedDistance = Math.ceil(calculatedDistance);
    }
  }
  return formattedDistance;
}

/**
 * Format the matched substring provided by google
 * @return {String} Formatted string with highlighted matched text
 */
export function formattedSuggestItemText(matchedSubstrings, mainText) {
  if (!matchedSubstrings) {
    return { matchedSubstring: mainText };
  }

  const start = matchedSubstrings.offset;
  const { length } = matchedSubstrings;
  const end = start + length;

  const pre = start > 0 ? mainText.slice(0, start) : '';
  const matchedSubstring = mainText.substring(start, end);
  const post = end < mainText.length ? mainText.slice(end) : '';

  return {
    pre,
    matchedSubstring,
    post,
  };
}

/**
 * calculate page amount
 * @param {Array} dataArray
 * @param {Int} pageSize
 */
export function calculatePages(dataArray, pageSize) {
  if (isEmpty(dataArray)) {
    return 0;
  }
  if (dataArray.length % pageSize !== 0) {
    return Math.floor(dataArray.length / pageSize) + 1;
  }
  return dataArray.length / pageSize;
}
